import { motion, AnimatePresence } from 'framer-motion'
import type { TMState } from '../engine/types'
import { tapeToArray } from '../engine/turing'

const CELL_SIZE = 44
const CELL_GAP = 2

interface TapeRendererProps {
  state: TMState
  blank: string
  reducedMotion: boolean
}

export function TapeRenderer({ state, blank, reducedMotion }: TapeRendererProps) {
  const { cells, headIndex } = tapeToArray(state, blank, 3)

  const totalWidth = cells.length * (CELL_SIZE + CELL_GAP) - CELL_GAP
  const height = 100
  const tapeY = 30

  return (
    <svg
      role="img"
      aria-label={`Turing tape at transition ${state.step}; head at cell ${state.head}, machine state ${state.state}, outcome ${state.outcome}.`}
      width="100%"
      viewBox={`-20 0 ${totalWidth + 40} ${height}`}
      style={{ overflow: 'visible', maxWidth: '600px' }}
    >
      <desc>
        {`Turing tape at transition ${state.step}; head at cell ${state.head}, machine state ${state.state}, outcome ${state.outcome}.`}
      </desc>
      <AnimatePresence>
        {cells.map((cell, i) => {
          const x = i * (CELL_SIZE + CELL_GAP)
          const isHead = i === headIndex

          return (
            <motion.g
              key={cell.position}
              initial={reducedMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={reducedMotion ? undefined : { opacity: 0 }}
              transition={{ duration: reducedMotion ? 0 : 0.2 }}
            >
              {/* Cell background */}
              <motion.rect
                x={x}
                y={tapeY}
                width={CELL_SIZE}
                height={CELL_SIZE}
                rx={3}
                fill={isHead ? 'var(--color-text)' : 'var(--color-bg)'}
                stroke="var(--color-border)"
                strokeWidth={1.5}
                animate={{
                  fill: isHead ? 'var(--color-text)' : 'var(--color-bg)',
                }}
                transition={{ duration: reducedMotion ? 0 : 0.3 }}
              />

              {/* Cell symbol */}
              <motion.text
                x={x + CELL_SIZE / 2}
                y={tapeY + CELL_SIZE / 2}
                textAnchor="middle"
                dominantBaseline="central"
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '16px',
                  fill: isHead ? 'var(--color-bg)' : 'var(--color-text)',
                  userSelect: 'none',
                }}
                animate={{
                  fill: isHead ? 'var(--color-bg)' : 'var(--color-text)',
                }}
                transition={{ duration: reducedMotion ? 0 : 0.3 }}
              >
                {cell.symbol === blank ? '' : cell.symbol}
              </motion.text>
            </motion.g>
          )
        })}
      </AnimatePresence>

      {/* Head indicator */}
      <motion.g
        animate={{ x: headIndex * (CELL_SIZE + CELL_GAP) + CELL_SIZE / 2 }}
        transition={{ duration: reducedMotion ? 0 : 0.3, ease: [0.16, 1, 0.3, 1] }}
      >
        <text
          x={0}
          y={tapeY - 6}
          textAnchor="middle"
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '12px',
            fill: 'var(--color-text)',
          }}
        >
          {state.state}
        </text>
        <polygon
          points="-5,22 5,22 0,28"
          fill="var(--color-text)"
          transform={`translate(0, ${tapeY - 28})`}
        />
      </motion.g>
    </svg>
  )
}
